import React from 'react';
import { Box, Typography, CircularProgress, alpha } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { textScale } from '../../theme/standards';

export interface LoadingState {
  isLoading: boolean;
  message?: string;
  messages?: string[]; // Array of rotating messages
  submessage?: string;
  variant?: 'activities' | 'restaurants' | 'details' | 'default';
}

interface TopLoadingIndicatorProps {
  loadingStates: LoadingState[];
}

const getVariantColor = (variant: LoadingState['variant'], theme: any) => {
  switch (variant) {
    case 'activities':
      return theme.palette.secondary.main;
    case 'restaurants':
      return '#FF6B6B';
    case 'details':
      return theme.palette.primary.main;
    default:
      return theme.palette.primary.main;
  }
};

export const TopLoadingIndicator: React.FC<TopLoadingIndicatorProps> = ({ loadingStates }) => {
  const theme = useTheme();
  const [messageIndex, setMessageIndex] = React.useState(0);
  
  // Find the first active loading state
  const activeLoading = loadingStates.find(state => state.isLoading);
  
  // Rotate through messages every 8 seconds (forward only, no repeating)
  React.useEffect(() => {
    if (!activeLoading?.messages || activeLoading.messages.length <= 1) {
      return;
    }
    
    const interval = setInterval(() => {
      setMessageIndex(prev => {
        // Only advance if not at the last message
        if (prev < activeLoading.messages!.length - 1) {
          return prev + 1;
        }
        // Stay at the last message
        return prev;
      });
    }, 8000);
    
    return () => clearInterval(interval);
  }, [activeLoading?.messages]);
  
  // Reset message index when loading state changes
  React.useEffect(() => {
    setMessageIndex(0);
  }, [activeLoading?.variant]);
  
  if (!activeLoading) {
    return null;
  }

  const color = getVariantColor(activeLoading.variant, theme);
  
  // Use rotating messages if available, otherwise use single message
  const displayMessage = activeLoading.messages 
    ? activeLoading.messages[messageIndex]
    : (activeLoading.message || 'Loading...');

  return (
    <Box
      sx={{
        position: 'fixed',
        top: { xs: 70, sm: 80 },
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9999,
        animation: activeLoading.isLoading ? 'slideDown 0.3s ease-out' : 'slideUp 0.2s ease-in',
        '@keyframes slideDown': {
          from: {
            opacity: 0,
            transform: 'translateX(-50%) translateY(-20px)',
          },
          to: {
            opacity: 1,
            transform: 'translateX(-50%) translateY(0)',
          },
        },
        '@keyframes slideUp': {
          from: {
            opacity: 1,
            transform: 'translateX(-50%) translateY(0)',
          },
          to: {
            opacity: 0,
            transform: 'translateX(-50%) translateY(-20px)',
          },
        },
      }}
    >
      <Box
        sx={{
          backgroundColor: alpha('#FFFFFF', 0.98),
          backdropFilter: 'blur(10px)',
          borderRadius: 2,
          border: `1px solid ${alpha(color, 0.15)}`,
          boxShadow: `0 4px 12px ${alpha('#000000', 0.08)}, 0 0 0 1px ${alpha(color, 0.05)}`,
          overflow: 'hidden',
          minWidth: { xs: 320, sm: 400 },
          maxWidth: { xs: '90vw', sm: 500 },
        }}
      >
        {/* Thin horizontal progress bar at the top */}
        <Box
          sx={{
            width: '100%',
            height: 3,
            backgroundColor: alpha(color, 0.12),
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              bottom: 0,
              width: '50%',
              background: `linear-gradient(90deg, transparent, ${color}, transparent)`,
              animation: 'slideRight 1.8s ease-in-out infinite',
              '@keyframes slideRight': {
                '0%': {
                  transform: 'translateX(-100%)',
                },
                '100%': {
                  transform: 'translateX(300%)',
                },
              },
            }}
          />
        </Box>

        {/* Content area */}
        <Box
          sx={{
            px: 2,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          {/* Text content */}
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontWeight: 500,
              fontSize: textScale.support.fontSize,
              letterSpacing: '0.01em',
              flex: 1,
              transition: 'opacity 0.3s ease',
            }}
            key={messageIndex}
          >
            {displayMessage}
          </Typography>

          {/* Small circular progress indicator */}
          <CircularProgress
            size={14}
            thickness={5}
            sx={{
              color: color,
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};
